import React, { useEffect, useState } from "react";
import { Calendar, Users, Building2, ShoppingCart, DollarSign, TrendingUp, TrendingDown, Award, Crown, Star, IndianRupee } from "lucide-react";
import { getTenantDashboardReportApi } from "../../services/reports";
import { useParams } from "react-router-dom";

// Mock data for demonstration
const mockTenantDashboardData = {
  totalCompanies: 12,
  totalClients: 1847,
  totalRevenue: 245680,
  totalOrders: 432,
  
  // Companies with earnings
  companiesWithEarnings: [
    { id: 1, name: "Tech Solutions Inc", earnings: 45680, orders: 89, clients: 234 },
    { id: 2, name: "Marketing Masters", earnings: 38950, orders: 76, clients: 189 },
    { id: 3, name: "Design Studios", earnings: 32100, orders: 65, clients: 167 },
    { id: 4, name: "Retail Giants", earnings: 28750, orders: 58, clients: 145 },
    { id: 5, name: "Food Ventures", earnings: 25200, orders: 51, clients: 123 },
    { id: 6, name: "Healthcare Plus", earnings: 22400, orders: 45, clients: 112 },
    { id: 7, name: "Education Hub", earnings: 19800, orders: 42, clients: 98 },
    { id: 8, name: "Finance Corp", earnings: 16500, orders: 38, clients: 87 }
  ],

  // Top performing employees
  topPerformers: [
    { id: 1, name: "John Smith", clientsHandled: 156, orders: 89, revenue: 34580, company: "Tech Solutions Inc" },
    { id: 2, name: "Sarah Johnson", clientsHandled: 142, orders: 78, revenue: 31200, company: "Marketing Masters" },
    { id: 3, name: "Mike Chen", clientsHandled: 134, orders: 71, revenue: 28940, company: "Design Studios" },
    { id: 4, name: "Emily Davis", clientsHandled: 128, orders: 67, revenue: 26750, company: "Retail Giants" },
    { id: 5, name: "Alex Rodriguez", clientsHandled: 119, orders: 62, revenue: 24100, company: "Food Ventures" }
  ],

  // High order companies (most orders)
  highOrderCompanies: [
    { id: 1, name: "Tech Solutions Inc", orders: 89, avgOrderValue: 513 },
    { id: 2, name: "Marketing Masters", orders: 76, avgOrderValue: 512 },
    { id: 3, name: "Design Studios", orders: 65, avgOrderValue: 494 },
    { id: 4, name: "Retail Giants", orders: 58, avgOrderValue: 496 },
    { id: 5, name: "Food Ventures", orders: 51, avgOrderValue: 494 }
  ]
};

const MetricCard = ({ title, value, icon: Icon, color = "blue", subtitle, trend }) => {
  const colorClasses = {
    blue: "bg-blue-50 border-blue-200 text-blue-900",
    green: "bg-green-50 border-green-200 text-green-900",
    purple: "bg-purple-50 border-purple-200 text-purple-900",
    orange: "bg-orange-50 border-orange-200 text-orange-900",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-900"
  };

  const iconColorClasses = {
    blue: "text-blue-600",
    green: "text-green-600",
    purple: "text-purple-600",
    orange: "text-orange-600",
    indigo: "text-indigo-600"
  };

  return (
    <div className={`p-6 rounded-lg border-2 ${colorClasses[color]} transition-all duration-200 hover:shadow-md`}>
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-full bg-white ${iconColorClasses[color]}`}>
          <Icon size={24} />
        </div>
        {trend && (
          <div className={`flex items-center text-sm ${trend.type === 'up' ? 'text-green-600' : 'text-red-600'}`}>
            {trend.type === 'up' ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
            <span className="ml-1">{trend.value}%</span>
          </div>
        )}
      </div>
      <div>
        <p className="text-sm font-medium opacity-70 mb-1">{title}</p>
        <p className="text-3xl font-bold mb-1">{typeof value === 'number' ? value.toLocaleString() : value}</p>
        {subtitle && <p className="text-sm opacity-60">{subtitle}</p>}
      </div>
    </div>
  );
};

const DateRangePicker = ({ startDate, endDate, onDateChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}-${day}-${year}`;
  };

  const handleApply = () => {
    if (tempStartDate && tempEndDate) {
      onDateChange(formatDate(tempStartDate), formatDate(tempEndDate));
      setIsOpen(false);
    }
  };

  const getDisplayText = () => {
    if (startDate && endDate) {
      return `${startDate} - ${endDate}`;
    }
    return "Select Date Range";
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        <Calendar size={18} />
        <span>{getDisplayText()}</span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 p-4 bg-white border border-gray-300 rounded-lg shadow-lg z-10 min-w-80">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={tempStartDate}
                onChange={(e) => setTempStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={tempEndDate}
                onChange={(e) => setTempEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <button
              onClick={() => setIsOpen(false)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const CompanyEarningsTable = ({ companies }) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-xl font-semibold text-gray-900 mb-6">Company Performance & Earnings</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 font-medium text-gray-700">Rank</th>
              <th className="text-left py-3 px-4 font-medium text-gray-700">Company Name</th>
              <th className="text-right py-3 px-4 font-medium text-gray-700">Earnings</th>
              <th className="text-right py-3 px-4 font-medium text-gray-700">Orders</th>
              <th className="text-right py-3 px-4 font-medium text-gray-700">Clients</th>
              <th className="text-right py-3 px-4 font-medium text-gray-700">Avg. Order</th>
            </tr>
          </thead>
          <tbody>
            {companies.map((company, index) => (
              <tr key={company.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-4">
                  <div className="flex items-center">
                    {index === 0 && <Crown className="text-yellow-500 mr-2" size={16} />}
                    {index === 1 && <Award className="text-gray-400 mr-2" size={16} />}
                    {index === 2 && <Award className="text-orange-600 mr-2" size={16} />}
                    <span className="font-medium">{index + 1}</span>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <div className="font-medium text-gray-900">{company.name}</div>
                </td>
                <td className="py-3 px-4 text-right">
                  <span className="font-semibold text-green-600">₹{company.earnings.toLocaleString()}</span>
                </td>
                <td className="py-3 px-4 text-right">
                  <span className="font-medium text-blue-600">{company.orders}</span>
                </td>
                <td className="py-3 px-4 text-right">
                  <span className="font-medium text-purple-600">{company.clients}</span>
                </td>
                <td className="py-3 px-4 text-right">
                  <span className="text-gray-700">₹{Math.round(company.earnings / company.orders)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TopPerformersCard = ({ performers }) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
        <Star className="text-yellow-500 mr-2" size={24} />
        Top Performing Employees
      </h3>
      <div className="space-y-4">
        {performers.slice(0, 5).map((performer, index) => (
          <div key={performer.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0 mr-4">
                {index === 0 && (
                  <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
                    <Crown className="text-white" size={20} />
                  </div>
                )}
                {index === 1 && (
                  <div className="w-10 h-10 bg-gray-400 rounded-full flex items-center justify-center">
                    <Award className="text-white" size={20} />
                  </div>
                )}
                {index === 2 && (
                  <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center">
                    <Award className="text-white" size={20} />
                  </div>
                )}
                {index > 2 && (
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                    {index + 1}
                  </div>
                )}
              </div>
              <div>
                <p className="font-semibold text-gray-900">{performer.name}</p>
                <p className="text-sm text-gray-600">{performer.company}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-semibold text-blue-600">{performer.clientsHandled} clients</p>
              <p className="text-sm text-gray-600">{performer.orders} orders • ₹{performer.revenue.toLocaleString()}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const HighOrderCompaniesCard = ({ companies }) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-xl font-semibold text-gray-900 mb-6">Highest Order Volume Companies</h3>
      <div className="space-y-3">
        {companies.slice(0, 5).map((company, index) => (
          <div key={company.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-blue-600 font-semibold text-sm">{index + 1}</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">{company.name}</p>
                <p className="text-sm text-gray-600">Avg: ₹{company.avgOrderValue}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-semibold text-orange-600">{company.orders} orders</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function TenantHome() {
  // For demo purposes, using a mock tenantId. In real app, get from useParams()
//   const tenantId = "demo-tenant-123";
  const todaySt = new Date().toISOString().split('T')[0];

  const today = new Date();
const oneMonthAgo = new Date(today);
oneMonthAgo.setMonth(today.getMonth() - 1);
const oneMonthAgoString = oneMonthAgo.toISOString().split('T')[0];
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterDate, setFilterDate] = useState({
    startDate: oneMonthAgoString,
    endDate: todaySt
  });
  let {tenantId} = useParams()
  useEffect(()=>{
getReports()
  },[filterDate.endDate])
  const getReports = async()=>{
    let res = await getTenantDashboardReportApi({...filterDate,tenantId})
    console.log(res?.DateRangePicker,"response of tenant dash")
    setDashboardData(res?.data)
  }
  const formatDateForAPI = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}-${day}-${year}`;
  };

  const getTenantDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const apiDateFilter = {
        startDate: formatDateForAPI(filterDate.startDate),
        endDate: formatDateForAPI(filterDate.endDate),
        tenantId: tenantId
      };

      // Uncomment when API is ready
      // const res = await getTenantDashboardReport(apiDateFilter);
      // if (res?.success && res?.data) {
      //   setDashboardData(res.data);
      // } else {
      //   setError('Failed to fetch dashboard data');
      // }

      // Mock data for demo
      setTimeout(() => {
        const variation = Math.random() * 0.3 + 0.85;
        setDashboardData({
          ...mockTenantDashboardData,
          totalCompanies: Math.floor(mockTenantDashboardData.totalCompanies * variation),
          totalClients: Math.floor(mockTenantDashboardData.totalClients * variation),
          totalRevenue: Math.floor(mockTenantDashboardData.totalRevenue * variation),
          totalOrders: Math.floor(mockTenantDashboardData.totalOrders * variation),
          companiesWithEarnings: mockTenantDashboardData.companiesWithEarnings.map(company => ({
            ...company,
            earnings: Math.floor(company.earnings * variation),
            orders: Math.floor(company.orders * variation),
            clients: Math.floor(company.clients * variation)
          }))
        });
        setLoading(false);
      }, 800);

    } catch (error) {
      console.error('Error fetching tenant dashboard:', error);
      setError('Error loading dashboard data');
      setLoading(false);
    }
  };

  const handleDateChange = (startDate, endDate) => {
    setFilterDate({ startDate, endDate });
  };

  useEffect(() => {
    getTenantDashboard();
  }, [filterDate, tenantId]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Tenant Dashboard</h1>
            <p className="text-gray-600 mt-1">
              Business overview and performance insights
              {filterDate.startDate === filterDate.endDate 
                ? ` for ${filterDate.startDate}`
                : ` from ${filterDate.startDate} to ${filterDate.endDate}`
              }
            </p>
          </div>
          <DateRangePicker
            startDate={filterDate.startDate}
            endDate={filterDate.endDate}
            onDateChange={handleDateChange}
          />
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={getTenantDashboard}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Main Content */}
        {!loading && !error && dashboardData && (
          <>
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
              <MetricCard
                title="Total Companies"
                value={dashboardData.totalCompanies}
                icon={Building2}
                color="blue"
                subtitle={`${Math.round(dashboardData.totalClients / dashboardData.totalCompanies)} clients avg.`}
              />
              <MetricCard
                title="Total Clients"
                value={dashboardData.totalClients}
                icon={Users}
                color="green"
                subtitle={`${Math.round(dashboardData.totalOrders / dashboardData.totalClients * 100) / 100} orders avg.`}
              />
              <MetricCard
                title="Total Revenue"
                value={`₹${dashboardData.totalRevenue.toLocaleString()}`}
                icon={IndianRupee}
                color="purple"
                subtitle={`₹${Math.round(dashboardData.totalRevenue / dashboardData.totalOrders)} avg. order`}
              />
              <MetricCard
                title="Total Orders"
                value={dashboardData.totalOrders}
                icon={ShoppingCart}
                color="orange"
                subtitle={`${Math.round(dashboardData.totalOrders / dashboardData.totalCompanies)} per company`}
              />
            </div>

            {/* Main Dashboard Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
              {/* Companies Earnings Table */}
              <div className="xl:col-span-2">
                <CompanyEarningsTable companies={dashboardData.companiesWithEarnings} />
              </div>

              {/* Side Panel */}
              <div className="space-y-6">
                <HighOrderCompaniesCard companies={dashboardData.highOrderCompanies} />
                <TopPerformersCard performers={dashboardData.topPerformers} />
              </div>
            </div>

            {/* Performance Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Revenue per Company</h4>
                <p className="text-3xl font-bold text-green-600">
                  ₹{Math.round(dashboardData.totalRevenue / dashboardData.totalCompanies).toLocaleString()}
                </p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Revenue per Client</h4>
                <p className="text-3xl font-bold text-blue-600">
                  ₹{Math.round(dashboardData.totalRevenue / dashboardData.totalClients)}
                </p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Orders per Client</h4>
                <p className="text-3xl font-bold text-purple-600">
                  {(dashboardData.totalOrders / dashboardData.totalClients).toFixed(1)}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}